"use client";

import { EmptyWallet, NotificationBing } from "iconsax-reactjs";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import ArandaoLogo from "@/assets/logo.svg";
import { ConnectButton } from "@/components/core";
import { Button } from "@/components/ui/button";
import { MenuIcon } from "@/components/ui/MenuIcon";
import { useSidebar } from "@/context/sidebar";
import { useWallet } from "@/context/wallet";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { Sidebar } from "../../features/landing/Sidebar/Sidebar";
import { SearchInput } from "./SearchInput";
import { SearchResultsOverlay } from "./SearchResultsOverlay";
import { SEARCH_MIN_CHARS } from "./search-config";
import { TopNavLogin } from "./TopNavLogin";

interface TopNavProps {
  className?: string;
}

export const TopNav: React.FC<TopNavProps> = ({ className }) => {
  const t = useTranslations("nav");
  const [searchValue, setSearchValue] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [navHeight, setNavHeight] = useState(0);
  const [sidebarMounted, setSidebarMounted] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const { isConnected, open } = useWallet();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const sidebar = useSidebar();
  const isOnSearchPage = pathname === "/search";

  // Mount Sidebar on first open (stays mounted for close animation)
  useEffect(() => {
    if (sidebar.isOpen && !sidebarMounted) {
      setSidebarMounted(true);
    }
  }, [sidebar.isOpen, sidebarMounted]);

  // Sync search value with URL query parameter when on search page
  useEffect(() => {
    if (isOnSearchPage) {
      const queryParam = searchParams.get("q");
      if (queryParam) {
        setSearchValue(queryParam);
        setIsSearchExpanded(true);
      }
    } else {
      // Clear search when leaving search page
      setSearchValue("");
      setIsSearchExpanded(false);
    }
  }, [isOnSearchPage, searchParams]);

  // Search expansion trigger - threshold at SEARCH_MIN_CHARS
  useEffect(() => {
    if (!isOnSearchPage) {
      setIsSearchExpanded(searchValue.length >= SEARCH_MIN_CHARS);
    }
  }, [searchValue, isOnSearchPage]);

  // Disable body scroll when search overlay is visible
  useEffect(() => {
    const isOverlayVisible =
      isSearchExpanded &&
      (!isOnSearchPage || searchValue !== searchParams.get("q"));

    if (isOverlayVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isSearchExpanded, isOnSearchPage, searchValue, searchParams]);

  // Get nav height for positioning the search overlay
  useEffect(() => {
    const updateNavHeight = () => {
      if (navRef.current) {
        setNavHeight(navRef.current.offsetHeight);
      }
    };

    updateNavHeight();
    window.addEventListener("resize", updateNavHeight);
    return () => window.removeEventListener("resize", updateNavHeight);
  }, []);

  const handleCancelSearch = () => {
    setSearchValue("");
    setIsSearchExpanded(false);
    // If on search page, navigate back to home
    if (isOnSearchPage) {
      router.push("/");
    }
  };

  return (
    <>
      <nav
        ref={navRef}
        className={cn(
          "bg-background border-b border-ins border-divider",
          "px-4 py-2",
          "flex flex-row-reverse items-center justify-between gap-4",
          "relative z-10",
          className,
        )}
      >
        {/* Left side nav elements */}
        <div
          className={cn(
            "flex items-center gap-4 shrink-0",
            "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
            isSearchExpanded
              ? "opacity-0 -translate-x-5 blur-[4px] pointer-events-none w-0 overflow-hidden"
              : "opacity-100 translate-x-0 blur-0",
          )}
        >
          <ConnectButton>{t("connect")}</ConnectButton>
          {isConnected && (
            <div className="flex items-center justify-center">
              <Button variant={"light"} size={"icon"} className="py-1.5">
                <NotificationBing size={"24"} variant="Linear" />
              </Button>
              <Button
                variant={"light"}
                size={"icon"}
                className="py-1.5"
                onClick={() => open()}
              >
                <EmptyWallet size={"24"} variant="Linear" />
              </Button>
            </div>
          )}
          <TopNavLogin />
        </div>

        {/* Search Input Container */}
        <SearchInput
          value={searchValue}
          onChange={setSearchValue}
          isExpanded={isSearchExpanded}
          onCancel={handleCancelSearch}
        />

        {/* Right side nav elements */}
        <div
          className={cn(
            "flex items-center gap-4 shrink-0",
            "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
            isSearchExpanded
              ? "opacity-0 translate-x-5 blur-[4px] pointer-events-none w-0 overflow-hidden"
              : "opacity-100 translate-x-0 blur-0",
          )}
        >
          {/* Menu Button */}
          <Button
            variant={"light"}
            size={"icon"}
            className="p-0 py-1.5 lg:hidden"
            aria-label="open menu"
            onClick={() => sidebar.setIsOpen(true)}
          >
            <MenuIcon size={24} />
          </Button>

          {/* Logo */}
          <Link href="/" className="flex items-center justify-center py-2">
            <ArandaoLogo
              className="size-[20px] text-foreground"
              aria-label="logo"
            />
          </Link>

          {sidebarMounted && <Sidebar />}
        </div>
      </nav>

      {/* Search Result Page Overlay - hide when on search page unless search value changed */}
      <SearchResultsOverlay
        isVisible={
          isSearchExpanded &&
          (!isOnSearchPage || searchValue !== searchParams.get("q"))
        }
        searchValue={searchValue}
        navHeight={navHeight}
        onResultClick={handleCancelSearch}
      />
    </>
  );
};
